"use client";

import React, { useState } from "react";
import { Check, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pricingModels = [{
  id: "diy",
  title: "DIY SaaS Model",
  subtitle: "Do It Yourself",
  monthlyPrice: "$97",
  annualPrice: "$970",
  period: "/mo",
  description: "Access to the platform with self-guided training and basic templates. Starting at:",
  isFeatured: false,
  buttonText: "Start Building",
  buttonVariant: "outline",
  linkTo: "https://links.agssuite.com/widget/form/0kYf5fvvvIeS2gTSy86O"
}, {
  id: "dfy",
  title: "DFY SaaS Setup",
  subtitle: "Done For You",
  monthlyPrice: "$2,497",
  annualPrice: "$19,970",
  period: "/mo",
  description: "Complete white-glove setup, Stripe & A2P compliance, and custom automation workflows.",
  isFeatured: true,
  buttonText: "Partner With Us",
  buttonVariant: "default",
  linkTo: "https://links.agssuite.com/widget/form/0kYf5fvvvIeS2gTSy86O"
}, {
  id: "dwy",
  title: "DWY SaaS Support",
  subtitle: "Done With You",
  monthlyPrice: "$597",
  annualPrice: "$4,970",
  period: "/mo",
  description: "Platform access plus expert guidance, weekly consulting, and compliance support. Starting at:",
  isFeatured: false,
  buttonText: "Get Guided Support",
  buttonVariant: "default",
  linkTo: "https://links.agssuite.com/widget/form/0kYf5fvvvIeS2gTSy86O"
}];
const features = [{
  modelId: "diy",
  text: "Core Platform Access"
}, {
  modelId: "diy",
  text: "Self-Guided Training Materials"
}, {
  modelId: "diy",
  text: "Basic Email Support"
}, {
  modelId: "diy",
  text: "Standard Community Access"
}, {
  modelId: "dwy",
  text: "Core Platform Access"
}, {
  modelId: "dwy",
  text: "Weekly Consulting Sessions"
}, {
  modelId: "dwy",
  text: "Stripe & A2P Compliance Guidance"
}, {
  modelId: "dwy",
  text: "Priority Email & Chat Support"
}, {
  modelId: "dwy",
  text: "Business Growth Strategy"
}, {
  modelId: "dfy",
  text: "Fully Managed Automated Growth Systems Setup"
}, {
  modelId: "dfy",
  text: "Done-For-You Stripe & A2P Compliance"
}, {
  modelId: "dfy",
  text: "Custom Business Automation Workflows"
}, {
  modelId: "dfy",
  text: "Dedicated Account Manager"
}, {
  modelId: "dfy",
  text: "24/7 Priority Support"
}, {
  modelId: "dfy",
  text: "Lead Generation Optimization"
}];
export default function SaasDeliveryModels() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="saas-delivery-models" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 transform ${isSectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="outline" className="mb-4 border-secondary/50 text-secondary font-default tracking-wide uppercase">
            <Zap className="size-3 mr-1" /> Delivery Models
          </Badge>
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-6 tracking-tight">
            Choose Your Level of <span className="text-primary">Partnership</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default mb-8">
            Whether you want to build it yourself, need expert guidance, or prefer a complete white-glove setup, AGS Partners has a delivery model tailored for your growth.
          </p>

          <div className="flex justify-center">
            <ToggleGroup type="single" value={billingCycle} onValueChange={value => value && setBillingCycle(value as "monthly" | "annual")} className="bg-card border border-border p-1 rounded-lg">
              <ToggleGroupItem value="monthly" className="px-6 py-2 rounded-md font-default data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all">
                Monthly
              </ToggleGroupItem>
              <ToggleGroupItem value="annual" className="px-6 py-2 rounded-md font-default data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all">
                Annual <span className="ml-1 text-xs opacity-80">(Save 20%)</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 items-center">
          {pricingModels.map((model, index) => {
          const modelFeatures = features.filter(f => f.modelId === model.id);
          const isFeatured = model.isFeatured;
          return <div key={model.id} data-index={index} className={`transition-all duration-700 transform ${isSectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
            transitionDelay: `${index * 150}ms`
          }}>
                <Card className={`relative flex flex-col h-full bg-card/80 backdrop-blur-sm border-border transition-all duration-300 hover:-translate-y-2 ${isFeatured ? "border-primary shadow-[0_0_40px_-10px_var(--color-primary)] md:-mt-8 md:mb-8 z-10" : "hover:border-secondary/50 hover:shadow-[0_0_30px_-15px_var(--color-secondary)]"}`}>
                  {isFeatured && <div className="absolute -top-4 left-0 right-0 flex justify-center z-20">
                      <Badge className="bg-chart-3 hover:bg-chart-3/90 text-white border-none px-4 py-1 shadow-lg shadow-chart-3/20 font-default uppercase tracking-wider text-xs">
                        Verified Implementation
                      </Badge>
                    </div>}

                  <CardHeader className="text-center pt-10 pb-6 border-b border-border/50">
                    <CardTitle className="text-2xl font-default font-bold text-foreground mb-2">
                      {model.subtitle}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground font-default h-12">
                      {model.description}
                    </CardDescription>
                    <div className="mt-6 flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-default font-bold tracking-tighter text-foreground">
                        {billingCycle === "monthly" ? model.monthlyPrice : model.annualPrice}
                      </span>
                      <span className="text-muted-foreground font-default text-sm">
                        {billingCycle === "annual" ? "/yr" : model.period}
                      </span>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 pt-8 pb-8">
                    <ul className="space-y-4">
                      {modelFeatures.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-3">
                          <Check className="size-5 text-secondary shrink-0 mt-0.5" />
                          <span className="text-sm font-default text-foreground/90 leading-tight">
                            {feature.text}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pb-8 pt-0">
                    <Link to={model.linkTo} className="w-full">
                      <Button variant={model.buttonVariant as any} className={`w-full font-default font-semibold h-12 text-base transition-all ${isFeatured ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)]" : model.id === 'diy' ? "border-muted-foreground text-muted-foreground hover:text-foreground hover:border-foreground bg-transparent" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
                        {model.buttonText}
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}